"use client";

import { cn } from "@ui/lib";
import {
	DELTA_TONE_CLASSES,
	type DeltaToneOptions,
	deltaTone,
} from "@ui/lib/chart-palette";
import { ArrowDownIcon, ArrowRightIcon, ArrowUpIcon } from "lucide-react";

export interface DeltaBadgeProps extends DeltaToneOptions {
	/** Signed change over the period, already computed. */
	change: number | null | undefined;
	/** Rendered after the number. */
	suffix?: string;
	/**
	 * A descriptive change with no better direction (how many brands an answer
	 * names, for instance): the arrow still shows the sign, but the badge is
	 * always neutral-toned, because green or red would claim a judgment the
	 * metric does not carry.
	 */
	neutral?: boolean;
	className?: string;
}

/**
 * Period-over-period change. The two channels carry different facts and must
 * not be collapsed: the arrow shows which way the metric moved (its sign),
 * the colour shows whether that movement is *good* (`deltaTone`, which is
 * where `lowerIsBetter` applies). A falling lower-is-better metric therefore
 * reads "↓ green", never "↑ green". Graduated from the design lab (which
 * re-exports this component).
 */
export function DeltaBadge({
	change,
	suffix = "%",
	lowerIsBetter,
	epsilon = 0,
	neutral = false,
	className,
}: DeltaBadgeProps) {
	const tone = neutral
		? "neutral"
		: deltaTone(change, { lowerIsBetter, epsilon });
	const shown =
		change == null || Number.isNaN(change) ? null : Math.abs(change);
	const direction =
		change == null || Number.isNaN(change) || Math.abs(change) <= epsilon
			? "flat"
			: change > 0
				? "up"
				: "down";
	const Icon =
		direction === "up"
			? ArrowUpIcon
			: direction === "down"
				? ArrowDownIcon
				: ArrowRightIcon;

	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums",
				DELTA_TONE_CLASSES[tone],
				className,
			)}
		>
			<Icon className="size-3" aria-hidden />
			{shown === null ? "—" : `${shown.toLocaleString()}${suffix}`}
			<span className="sr-only">
				{direction === "flat"
					? "no meaningful change"
					: tone === "neutral"
						? direction === "up"
							? "increased"
							: "decreased"
						: `${direction === "up" ? "increased" : "decreased"}, ${
								tone === "positive" ? "improved" : "worsened"
							}`}
			</span>
		</span>
	);
}
